import React, { createContext, useContext, ReactNode, useReducer, useCallback, useEffect } from 'react';
import { 
  GridConfiguration, 
  GridBuilderState, 
  GridBuilderActions,
  GridSection,
  GridLayout,
  GridCardStyle,
  GridBuilderProviderProps
} from '@supply-mover-2-0/core';
import { FieldConfiguration } from '../../types';

interface GridBuilderContextValue {
  state: GridBuilderState;
  actions: GridBuilderActions;
}

const GridBuilderContext = createContext<GridBuilderContextValue | null>(null);

// Action types for the reducer
type GridBuilderAction =
  | { type: 'SET_GRID_CONFIG'; payload: GridConfiguration }
  | { type: 'UPDATE_SECTION'; payload: { sectionId: string; updates: Partial<GridSection> } }
  | { type: 'ADD_SECTION'; payload: Omit<GridSection, 'id' | 'order'> }
  | { type: 'REMOVE_SECTION'; payload: string }
  | { type: 'REORDER_SECTIONS'; payload: { startIndex: number; endIndex: number } }
  | { type: 'ADD_FIELD_TO_SECTION'; payload: { sectionId: string; field: FieldConfiguration; index?: number } }
  | { type: 'REMOVE_FIELD_FROM_SECTION'; payload: { sectionId: string; fieldId: string } }
  | { type: 'REORDER_FIELDS_IN_SECTION'; payload: { sectionId: string; startIndex: number; endIndex: number } }
  | { type: 'MOVE_FIELD_BETWEEN_SECTIONS'; payload: { fromSectionId: string; toSectionId: string; fieldId: string; toIndex?: number } }
  | { type: 'UPDATE_GRID_LAYOUT'; payload: Partial<GridLayout> }
  | { type: 'UPDATE_CARD_STYLE'; payload: Partial<GridCardStyle> }
  | { type: 'TOGGLE_CONFIG_MODE'; payload: boolean }
  | { type: 'SET_PREVIEW_MODE'; payload: GridBuilderState['previewMode'] }
  | { type: 'SET_SELECTED_SECTION'; payload: string | undefined }
  | { type: 'SET_DRAGGED_FIELD'; payload: string | undefined }
  | { type: 'SET_DRAGGED_SECTION'; payload: string | undefined };

// Initial state
const initialState: GridBuilderState = {
  gridConfig: null,
  isConfiguring: false,
  selectedSectionId: undefined,
  draggedFieldId: undefined,
  draggedSectionId: undefined,
  previewMode: 'md'
};

// Reducer function
function gridBuilderReducer(state: GridBuilderState, action: GridBuilderAction): GridBuilderState {
  switch (action.type) {
    case 'SET_GRID_CONFIG':
      return { ...state, gridConfig: action.payload };

    case 'UPDATE_SECTION': {
      if (!state.gridConfig) return state;
      const sections = state.gridConfig.sections.map(section =>
        section.id === action.payload.sectionId
          ? { ...section, ...action.payload.updates }
          : section
      );
      return {
        ...state,
        gridConfig: { ...state.gridConfig, sections }
      };
    }

    case 'ADD_SECTION': {
      if (!state.gridConfig) return state;
      const newSection: GridSection = {
        ...action.payload,
        id: `section-${Date.now()}`,
        order: state.gridConfig.sections.length + 1
      };
      return {
        ...state,
        gridConfig: {
          ...state.gridConfig,
          sections: [...state.gridConfig.sections, newSection]
        }
      };
    }

    case 'REMOVE_SECTION': {
      if (!state.gridConfig) return state;
      const sections = state.gridConfig.sections
        .filter(section => section.id !== action.payload)
        .map((section, index) => ({ ...section, order: index + 1 }));
      return {
        ...state,
        gridConfig: { ...state.gridConfig, sections }
      };
    }

    case 'REORDER_SECTIONS': {
      if (!state.gridConfig) return state;
      const sections = [...state.gridConfig.sections];
      const [removed] = sections.splice(action.payload.startIndex, 1);
      sections.splice(action.payload.endIndex, 0, removed);
      const reorderedSections = sections.map((section, index) => ({
        ...section,
        order: index + 1
      }));
      return {
        ...state,
        gridConfig: { ...state.gridConfig, sections: reorderedSections }
      };
    }

    case 'ADD_FIELD_TO_SECTION': {
      if (!state.gridConfig) return state;
      const sections = state.gridConfig.sections.map(section => {
        if (section.id === action.payload.sectionId) {
          const fields = [...section.fields];
          const index = action.payload.index ?? fields.length;
          fields.splice(index, 0, action.payload.field);
          return { ...section, fields };
        }
        return section;
      });
      return {
        ...state,
        gridConfig: { ...state.gridConfig, sections }
      };
    }

    case 'REMOVE_FIELD_FROM_SECTION': {
      if (!state.gridConfig) return state;
      const sections = state.gridConfig.sections.map(section => {
        if (section.id === action.payload.sectionId) {
          const fields = section.fields.filter(field => field.id !== action.payload.fieldId);
          return { ...section, fields };
        }
        return section;
      });
      return {
        ...state,
        gridConfig: { ...state.gridConfig, sections }
      };
    }

    case 'REORDER_FIELDS_IN_SECTION': {
      if (!state.gridConfig) return state;
      const sections = state.gridConfig.sections.map(section => {
        if (section.id === action.payload.sectionId) {
          const fields = [...section.fields];
          const [removed] = fields.splice(action.payload.startIndex, 1);
          fields.splice(action.payload.endIndex, 0, removed);
          return { ...section, fields };
        }
        return section;
      });
      return {
        ...state,
        gridConfig: { ...state.gridConfig, sections }
      };
    }

    case 'MOVE_FIELD_BETWEEN_SECTIONS': {
      if (!state.gridConfig) return state;
      let fieldToMove: FieldConfiguration | undefined;
      
      // Remove field from source section
      const sectionsAfterRemoval = state.gridConfig.sections.map(section => {
        if (section.id === action.payload.fromSectionId) {
          const fields = section.fields.filter(field => {
            if (field.id === action.payload.fieldId) {
              fieldToMove = field;
              return false;
            }
            return true;
          });
          return { ...section, fields };
        }
        return section;
      });

      if (!fieldToMove) return state;

      // Add field to target section
      const sections = sectionsAfterRemoval.map(section => {
        if (section.id === action.payload.toSectionId) {
          const fields = [...section.fields];
          const index = action.payload.toIndex ?? fields.length;
          fields.splice(index, 0, fieldToMove!);
          return { ...section, fields };
        }
        return section;
      });

      return {
        ...state,
        gridConfig: { ...state.gridConfig, sections }
      };
    }

    case 'UPDATE_GRID_LAYOUT': {
      if (!state.gridConfig) return state;
      return {
        ...state,
        gridConfig: {
          ...state.gridConfig,
          layout: { ...state.gridConfig.layout, ...action.payload }
        }
      };
    }

    case 'UPDATE_CARD_STYLE': {
      if (!state.gridConfig) return state;
      return {
        ...state,
        gridConfig: {
          ...state.gridConfig,
          cardStyle: { ...state.gridConfig.cardStyle, ...action.payload }
        }
      };
    }

    case 'TOGGLE_CONFIG_MODE':
      return { ...state, isConfiguring: action.payload };

    case 'SET_PREVIEW_MODE':
      return { ...state, previewMode: action.payload };

    case 'SET_SELECTED_SECTION':
      return { ...state, selectedSectionId: action.payload };

    case 'SET_DRAGGED_FIELD':
      return { ...state, draggedFieldId: action.payload };

    case 'SET_DRAGGED_SECTION':
      return { ...state, draggedSectionId: action.payload };

    default:
      return state;
  }
}

export function GridBuilderProvider({ children, initialConfig, onChange }: GridBuilderProviderProps) {
  const [state, dispatch] = useReducer(gridBuilderReducer, {
    ...initialState,
    gridConfig: initialConfig || null
  });

  // Notify parent of changes
  useEffect(() => {
    if (state.gridConfig && onChange) {
      onChange(state.gridConfig);
    }
  }, [state.gridConfig, onChange]);

  // Action creators
  const actions: GridBuilderActions = {
    setGridConfig: useCallback((config: GridConfiguration) => {
      dispatch({ type: 'SET_GRID_CONFIG', payload: config });
    }, []),

    updateSection: useCallback((sectionId: string, updates: Partial<GridSection>) => {
      dispatch({ type: 'UPDATE_SECTION', payload: { sectionId, updates } });
    }, []),

    addSection: useCallback((section: Omit<GridSection, 'id' | 'order'>) => {
      dispatch({ type: 'ADD_SECTION', payload: section });
    }, []),

    removeSection: useCallback((sectionId: string) => {
      dispatch({ type: 'REMOVE_SECTION', payload: sectionId });
    }, []),

    reorderSections: useCallback((startIndex: number, endIndex: number) => {
      dispatch({ type: 'REORDER_SECTIONS', payload: { startIndex, endIndex } });
    }, []),

    addFieldToSection: useCallback((sectionId: string, field: FieldConfiguration, index?: number) => {
      dispatch({ type: 'ADD_FIELD_TO_SECTION', payload: { sectionId, field, index } });
    }, []),

    removeFieldFromSection: useCallback((sectionId: string, fieldId: string) => {
      dispatch({ type: 'REMOVE_FIELD_FROM_SECTION', payload: { sectionId, fieldId } });
    }, []),

    reorderFieldsInSection: useCallback((sectionId: string, startIndex: number, endIndex: number) => {
      dispatch({ type: 'REORDER_FIELDS_IN_SECTION', payload: { sectionId, startIndex, endIndex } });
    }, []),

    moveFieldBetweenSections: useCallback((fromSectionId: string, toSectionId: string, fieldId: string, toIndex?: number) => {
      dispatch({ type: 'MOVE_FIELD_BETWEEN_SECTIONS', payload: { fromSectionId, toSectionId, fieldId, toIndex } });
    }, []),

    updateGridLayout: useCallback((layout: Partial<GridLayout>) => {
      dispatch({ type: 'UPDATE_GRID_LAYOUT', payload: layout });
    }, []),

    updateCardStyle: useCallback((style: Partial<GridCardStyle>) => {
      dispatch({ type: 'UPDATE_CARD_STYLE', payload: style });
    }, []),

    toggleConfigMode: useCallback((isConfiguring: boolean) => {
      dispatch({ type: 'TOGGLE_CONFIG_MODE', payload: isConfiguring });
    }, []),

    setPreviewMode: useCallback((mode: GridBuilderState['previewMode']) => {
      dispatch({ type: 'SET_PREVIEW_MODE', payload: mode });
    }, [])
  };

  const contextValue: GridBuilderContextValue = {
    state,
    actions
  };

  return (
    <GridBuilderContext.Provider value={contextValue}>
      {children}
    </GridBuilderContext.Provider>
  );
}

// Hook to use the grid builder context
export function useGridBuilder(): GridBuilderContextValue {
  const context = useContext(GridBuilderContext);
  if (!context) {
    throw new Error('useGridBuilder must be used within a GridBuilderProvider');
  }
  return context;
}

// Convenience hooks
export function useGridBuilderState() {
  const { state } = useGridBuilder();
  return state;
}

export function useGridBuilderActions() {
  const { actions } = useGridBuilder();
  return actions;
}

export function useGridConfig() {
  const { state } = useGridBuilder();
  return state.gridConfig;
}

export function useGridSections() {
  const { state } = useGridBuilder();
  return state.gridConfig?.sections || [];
}